import {UserModel} from "../../models/Admin/UserModel";
import {createContext, Dispatch, SetStateAction, useCallback, useContext, useEffect, useMemo, useState} from "react";
import {UserParamsModel} from "../../models/Admin/UserParamsModel";
import {PagesPaginateModel} from "../../models/PagesPaginate";
import {UserService} from "../../services/Admin/UserService";
import {useLocation} from "react-router-dom";
import {Generics} from "../../models/Fgts/Generics";
import {ActionAccessModel} from "../../models/Admin/ActionAccessModel";
import {AccessProfileEnum} from "../../models/Admin/AccessProfileEnum";
import {useStyle} from "../Style/StyleProvider";
import {useAuth} from "../AuthProvider";
import {useQuery} from "react-query";

interface ContextProps {
    users: UserModel[],
    setUsers: Dispatch<SetStateAction<UserModel[]>>,
    user: UserModel | undefined,
    setUser: Dispatch<SetStateAction<UserModel | undefined>>,
    typistsOptions: { value: any; label: any }[],
    usersOptions: { value: any; label: any }[],
    backofficeTypists: any[],
    error: string,
    setError: Dispatch<SetStateAction<string>>,
    pagesPaginate: PagesPaginateModel,
    setPagesPaginate: Dispatch<SetStateAction<PagesPaginateModel>>,
    parameters: UserParamsModel,
    setParameters: Dispatch<SetStateAction<UserParamsModel>>,
    handleDetails: any,
    create: (payload: UserModel) => Promise<any>,
    isLoading: boolean
}

export const UsuarioContext = createContext<ContextProps>({} as ContextProps);

export const UserProvider = (props: any) => {
    const [parameters, setParameters] = useState<UserParamsModel>({pageNumber: 1});
    const [users, setUsers] = useState<UserModel[]>([]);
    const [user, setUser] = useState<UserModel | undefined>(undefined);
    const [error, setError] = useState<string>('');
    const [pagesPaginate, setPagesPaginate] = useState<PagesPaginateModel>({});
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isTypistRole, setIsTypistRole] = useState<boolean>(false);
    const service = useMemo(() => new UserService(), []);
    const location = useLocation();
    const {style} = useStyle();
    const {CanAccess, userLogado} = useAuth();

    // Resolve typist role once
    useEffect(() => {
        CanAccess('usuarios.ap_vendedor.regra').then((result: boolean) => setIsTypistRole(result));
    }, []);

    const handleList = useCallback(async () => {
        if (!location.pathname.includes('/admin/usuario')) {
            return false;
        }

        try {
            setIsLoading(true);
            const [_Response, _Error] = await service.list(parameters);
            setIsLoading(false);
            if (!!_Error) {
                return setError(_Response || _Error);
            }

            setError('');
            setUsers(Generics.toArray<ActionAccessModel>(_Response.data));
            setPagesPaginate(_Response.pages);

            return true;

        } catch (err) {
            console.warn(err);
        }
    }, [location.pathname, parameters]);

    const handleDetails = async (id: number) => {
        try {
            setIsLoading(true);
            const [_Response, _Error] = await service.details(id);
            setIsLoading(false);
            if (!!_Error) {
                return setError(_Response || _Error);
            }

            setError('');
            setUser(_Response.data);

            return true;

        } catch (err) {
            console.warn(err);
        }
    };

    const create = async (payload: UserModel) => {
        try {
            setIsLoading(true);
            const [_Response, _Error] = payload.id ? await service.update(payload) : await service.create(payload);
            setIsLoading(false);

            if (!!_Error) {
                setError(_Response || _Error);
                return [false, (_Response || _Error)]
            }

            setError('');
            handleList().then();

            return [true, null];

        } catch (err) {
            return [false, null];
        }
    };

    // Single cached query for all users by company — replaces the old getTypists() call
    const {data: allUsersData} = useQuery<any[]>(
        ['allUsers', style?.id],
        async () => {
            const [result] = await service.list({companyId: style?.id});
            return result?.data ?? [];
        },
        {
            staleTime: 1000 * 60 * 5,
            refetchOnWindowFocus: false,
            enabled: !!style?.id,
        }
    );

    const typistsOptions = useMemo(() => {
        if (!allUsersData) return [];
        return allUsersData
            .filter((x: any) =>
                ((x.accessProfileId === AccessProfileEnum.BACKOFFICE || (isTypistRole && userLogado?.id === x.id)) && x.active)
            )
            .map((x: any) => ({value: x.id, label: x.name?.toUpperCase()}))
            .sort((a: any, b: any) => a.label < b.label ? -1 : 1);
    }, [allUsersData, isTypistRole, userLogado?.id]);

    const usersOptions = useMemo(() => {
        if (!allUsersData) return [];
        return allUsersData
            .filter((x: any) => x.active)
            .map((x: any) => ({value: x.id, label: x.name?.toUpperCase()}))
            .sort((a: any, b: any) => a.label < b.label ? -1 : 1);
    }, [allUsersData]);

    const backofficeTypists = useMemo(() => {
        if (!allUsersData) return [];
        return allUsersData
            .filter((x: any) => x.accessProfileId === AccessProfileEnum.BACKOFFICE && x.active)
            .map((x: any) => ({value: x.id, label: x.name?.toUpperCase()}))
            .sort((a: any, b: any) => a.label < b.label ? -1 : 1);
    }, [allUsersData]);

    useEffect(() => {
        handleList().then();
    }, [handleList]);

    const contextValue: ContextProps = {
        users,
        setUsers,
        user,
        setUser,
        typistsOptions,
        usersOptions,
        backofficeTypists,
        error,
        setError,
        pagesPaginate,
        setPagesPaginate,
        parameters,
        setParameters,
        handleDetails,
        create,
        isLoading,
    };

    return (
        <UsuarioContext.Provider value={contextValue}>
            {props.children}
        </UsuarioContext.Provider>
    );
}

export const useUser = () => useContext(UsuarioContext);